import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";

import { type ConnectorOption } from "./components/InputPlusMenu";

// Reduce a URL to its pathname so origin/signature/fragment differences do
// not prevent a match. Falls back to the trimmed input when URL parsing
// fails (e.g. relative paths).
function urlPathname(url: string | undefined | null): string {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    const q = url.indexOf("?");
    const h = url.indexOf("#");
    const end = Math.min(q >= 0 ? q : url.length, h >= 0 ? h : url.length);
    return url.slice(0, end);
  }
}

// Resolve a citation's source URL to an index in the context file list. Signed
// S3 URLs are compared by pathname because the query-string signature can
// differ between when the backend emitted the citation and when the frontend
// fetched the asset. Returns -1 when nothing matches (caller keeps the current
// file selection).
export function findContextFileIndex(
  contextFiles: readonly { source: string }[],
  sourceUrl: string | undefined,
  originalURL: string | undefined,
): number {
  const sourceKey = urlPathname(sourceUrl);
  const originalKey = urlPathname(originalURL);
  if (!sourceKey && !originalKey) return -1;
  for (let i = 0; i < contextFiles.length; i++) {
    const fileKey = urlPathname(contextFiles[i]?.source);
    if (!fileKey) continue;
    if (fileKey === sourceKey || fileKey === originalKey) return i;
  }
  return -1;
}

/**
 * Extract the chronologically-ordered list of unique review ticket IDs from
 * chat messages. Drives the "Review N of M" navigation when a single chat
 * has multiple sequential reviews.
 *
 * Only considers assistant messages with a viewTicketId. The first occurrence
 * of each ticketId wins so order is stable even if a ticket is referenced
 * again later in the conversation.
 */
export function collectReviewTicketIds(
  messages: readonly { role: string; viewTicketId?: string | null }[],
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const msg of messages) {
    if (
      msg.role === "assistant" &&
      msg.viewTicketId &&
      !seen.has(msg.viewTicketId)
    ) {
      seen.add(msg.viewTicketId);
      ids.push(msg.viewTicketId);
    }
  }
  return ids;
}

/**
 * Extract the chronologically-ordered list of unique assistant message IDs
 * that carry a non-empty canvas (≥1 block). Drives the "Canvas N of M" style
 * navigation when a single chat has multiple sequential canvases — the
 * canvas analogue of `collectReviewTicketIds`.
 *
 * First occurrence of each message id wins so order is stable.
 */
export function collectCanvasMessageIds(
  messages: readonly {
    _id: string;
    role: string;
    canvasContent?: { blocks?: unknown[] } | null;
  }[],
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const msg of messages) {
    if (
      msg.role === "assistant" &&
      msg.canvasContent &&
      Array.isArray(msg.canvasContent.blocks) &&
      msg.canvasContent.blocks.length > 0 &&
      !seen.has(msg._id)
    ) {
      seen.add(msg._id);
      ids.push(msg._id);
    }
  }
  return ids;
}

/**
 * Build the click handler that activates an older review ticket from the
 * chat history. Extracted into a factory so the index lookup + dismissed
 * clear + analytics payload can be unit-tested directly without mounting
 * `chat.tsx`.
 *
 * Stale ticket ids (e.g. a ticket deleted server-side after the chat
 * loaded) silently no-op: no state mutation, no analytics event. The
 * `TicketViewPane` already renders its own "Ticket not found" state, but
 * we want a stale click to be a non-event from the chat's perspective.
 */
export interface CreateActivateTicketHandlerDeps {
  allReviewTicketIds: readonly string[];
  setSelectedReviewIndex: (idx: number) => void;
  setTicketDismissed: (dismissed: boolean) => void;
  capture: (event: string, props: Record<string, unknown>) => void;
  chatId: string | undefined;
}

export function createActivateTicketHandler(
  deps: CreateActivateTicketHandlerDeps,
): (ticketId: string) => void {
  return (ticketId: string) => {
    const idx = deps.allReviewTicketIds.indexOf(ticketId);
    if (idx < 0) return;
    deps.setSelectedReviewIndex(idx);
    deps.setTicketDismissed(false);
    deps.capture("ask_puntt_previous_artifact_clicked", {
      artifact: "ticket",
      ticket_id: ticketId,
      chat_id: deps.chatId,
    });
  };
}

/**
 * D8 streaming-suppression predicate. Walks the latest assistant message's
 * `agentSteps`, pairing tool_calls with their tool_results positionally
 * per tool name. If at least one unresolved (open) tool_call has a tool
 * name in `artifactProducingTools`, returns true — older clickable artifact
 * rows should grey out so a stale click can't yank the right pane while
 * a new artifact is mid-stream.
 *
 * The check is intentionally narrow: it fires only when the LAST message
 * is an in-flight assistant turn. Completed turns don't block. A turn
 * that calls `puntt_search` (non-artifact) doesn't block.
 */
export function computeIsAnotherArtifactStreaming(
  messages: readonly {
    role: string;
    status?: string;
    agentSteps?: { type: string; tool?: string }[];
  }[],
  artifactProducingTools: ReadonlySet<string>,
): boolean {
  const lastMsg = messages[messages.length - 1];
  if (!lastMsg || lastMsg.role !== "assistant") return false;
  if (lastMsg.status === "completed") return false;
  if (!lastMsg.agentSteps) return false;
  const open = new Map<string, number>();
  for (const step of lastMsg.agentSteps) {
    if (step.type === "tool_call" && step.tool) {
      open.set(step.tool, (open.get(step.tool) ?? 0) + 1);
    } else if (step.type === "tool_result" && step.tool) {
      open.set(step.tool, (open.get(step.tool) ?? 0) - 1);
    }
  }
  let found = false;
  open.forEach((count, tool) => {
    if (count > 0 && artifactProducingTools.has(tool)) found = true;
  });
  return found;
}

/**
 * Canvas counterpart to `createActivateTicketHandler`. Stale message ids
 * (canvas wiped by a backend re-render, message dropped from `localMessages`,
 * etc.) no-op the same way so the click is never a destructive operation.
 */
export interface CreateActivateCanvasHandlerDeps {
  canvasMessageIds: readonly string[];
  setSelectedCanvasMessageId: (id: string | null) => void;
  setCanvasDismissed: (dismissed: boolean) => void;
  capture: (event: string, props: Record<string, unknown>) => void;
  chatId: string | undefined;
}

export function createActivateCanvasHandler(
  deps: CreateActivateCanvasHandlerDeps,
): (messageId: string) => void {
  return (messageId: string) => {
    if (!deps.canvasMessageIds.includes(messageId)) return;
    deps.setSelectedCanvasMessageId(messageId);
    deps.setCanvasDismissed(false);
    deps.capture("ask_puntt_previous_artifact_clicked", {
      artifact: "canvas",
      message_id: messageId,
      chat_id: deps.chatId,
    });
  };
}

/** Convert chat's enabledConnectors array to Record for display */
export function connectorsArrayToRecord(
  ids: string[] | undefined,
  available: ConnectorOption[],
): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  available.forEach((c) => {
    record[c.id] = ids?.includes(c.id) ?? false;
  });
  return record;
}

/**
 * Monotonic merge: never regress append-only arrays or clear fields
 * that haven't been replaced by final content yet. Prevents stale poll
 * responses from overwriting locally accumulated status data.
 */
export type AskPunttMessageExt = AskPunttMessage & {
  canvasChangedSectionIds?: string[];
  projectLoading?: boolean | null;
};

/**
 * Prefer the richer projectData object. If incoming is missing or emptier than
 * the local copy, keep local to avoid regressing progressively streamed fields.
 */
export function mergeProjectData(
  local: AskPunttMessage["projectData"],
  incoming: AskPunttMessage["projectData"],
): AskPunttMessage["projectData"] {
  if (!incoming) return local;
  if (!local) return incoming;
  const localKeys = Object.keys(local).filter(
    (k) => (local as Record<string, unknown>)[k] != null,
  );
  const incomingKeys = Object.keys(incoming).filter(
    (k) => (incoming as Record<string, unknown>)[k] != null,
  );
  return incomingKeys.length >= localKeys.length ? incoming : local;
}

export function mergeMessageStatus(
  local: AskPunttMessageExt,
  incoming: AskPunttMessageExt,
): AskPunttMessageExt {
  return {
    ...local,
    agentSteps:
      (incoming.agentSteps?.length ?? 0) >= (local.agentSteps?.length ?? 0)
        ? incoming.agentSteps
        : local.agentSteps,
    searchProgress:
      (incoming.searchProgress?.length ?? 0) >=
      (local.searchProgress?.length ?? 0)
        ? incoming.searchProgress
        : local.searchProgress,
    streamingContent:
      incoming.streamingContent !== null &&
      incoming.streamingContent !== undefined
        ? incoming.streamingContent
        : incoming.content
          ? incoming.streamingContent
          : local.streamingContent,
    content: incoming.content || local.content,
    status:
      local.status === "completed"
        ? "completed"
        : (incoming.status ?? local.status),
    structuredContent: incoming.structuredContent ?? local.structuredContent,
    canvasContent: incoming.canvasContent ?? local.canvasContent,
    canvasLoading: incoming.canvasContent
      ? incoming.canvasLoading
      : (incoming.canvasLoading ?? local.canvasLoading),
    canvasChangedSectionIds:
      incoming.canvasChangedSectionIds ?? local.canvasChangedSectionIds,
    viewTicketId: incoming.viewTicketId ?? local.viewTicketId,
    suggestedResponses: incoming.suggestedResponses ?? local.suggestedResponses,
    userQuestions: incoming.userQuestions ?? local.userQuestions,
    projectData: mergeProjectData(local.projectData, incoming.projectData),
    projectLoading: incoming.projectData
      ? incoming.projectLoading
      : (incoming.projectLoading ?? local.projectLoading),
    contentBlocks: incoming.contentBlocks ?? local.contentBlocks,
    feedback: incoming.feedback ?? local.feedback,
  };
}

/** Human-readable labels for Ask Puntt ``activeSkill`` values in the chat UI. */
export const ASK_PUNTT_SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
  compare: "Brief Comparison",
  project: "Project",
  puntt_actions: "Puntt Capabilities",
  puntt_value: "Workflow Analysis",
  meeting_roi: "ROI Workshop",
};
